/**
 * Environment Switcher Modal
 *
 * Modal dialog for switching between prod and dev environments.
 * Opened by clicking the environment badge in the header.
 */

import { useCallback, useEffect, useState } from 'react'
import { useEnvironmentModalStore } from '@/shared/stores/environment-modal-store'
import { useEnvironmentStore, type EnvironmentName } from '@/shared/stores/environment-store'
import '../../styles/environment-modal.css'

const ENV_LABEL: Record<EnvironmentName, string> = {
  prod: 'Production',
  dev: 'Development',
}

export function EnvironmentModal() {
  const isOpen = useEnvironmentModalStore((s) => s.isOpen)
  const closeModal = useEnvironmentModalStore((s) => s.closeModal)

  const active = useEnvironmentStore((s) => s.active)
  const configs = useEnvironmentStore((s) => s.configs)
  const switchEnvironment = useEnvironmentStore((s) => s.switchEnvironment)

  // Default selection to the OTHER (non-active) env so the action is clear
  const otherEnv: EnvironmentName = active === 'prod' ? 'dev' : 'prod'
  const [selectedEnv, setSelectedEnv] = useState<EnvironmentName>(otherEnv)

  // Reset selection to the other env each time modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedEnv(active === 'prod' ? 'dev' : 'prod')
    }
  }, [isOpen, active])

  // Close on Escape
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeModal()
      }
    },
    [isOpen, closeModal],
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  if (!isOpen || !configs) return null

  const selectedConfig = configs[selectedEnv]
  const isAlreadyActive = selectedEnv === active
  const isSelectedUnavailable = selectedConfig.unavailable
  const canSwitch = !isAlreadyActive && !isSelectedUnavailable

  const handleSwitch = () => {
    if (canSwitch) {
      switchEnvironment(selectedEnv)
      closeModal()
    }
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) closeModal()
  }

  // Button label adapts to current state
  let buttonLabel: string
  if (isAlreadyActive) {
    buttonLabel = `Already in ${ENV_LABEL[selectedEnv]}`
  } else if (isSelectedUnavailable) {
    buttonLabel = `${ENV_LABEL[selectedEnv]} Unavailable`
  } else {
    buttonLabel = `Switch to ${ENV_LABEL[selectedEnv]}`
  }

  const renderCard = (env: EnvironmentName) => {
    const config = configs[env]
    const isCurrent = active === env
    const isSelected = selectedEnv === env
    const isUnavailable = config.unavailable

    return (
      <button
        type="button"
        className={[
          'env-card',
          isSelected ? 'selected' : '',
          isUnavailable ? 'unavailable' : '',
          isCurrent ? 'current' : '',
        ].filter(Boolean).join(' ')}
        onClick={() => setSelectedEnv(env)}
        data-testid={`environment-card-${env}`}
        aria-pressed={isSelected}
      >
        <div className="env-card-header">
          <div className={`env-card-icon ${env}`}>
            {env === 'prod' ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
                <circle cx="8" cy="8" r="3" fill="currentColor" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M5 3L1 8l4 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M11 3l4 5-4 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
          <div className="env-card-info">
            <div className="env-card-name">{ENV_LABEL[env]}</div>
            <div className="env-card-label">kodeon-backend-{env}</div>
          </div>
          <div className="env-card-badges">
            {isCurrent && <div className="env-card-badge current">Current</div>}
            {isUnavailable && <div className="env-card-badge unavailable">Unavailable</div>}
          </div>
        </div>

        {!isUnavailable && config.gateway_url && (
          <div className="env-card-url">{config.gateway_url}</div>
        )}

        {isUnavailable && config.error && (
          <div className="env-card-error">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.2" />
              <path d="M6 3.5v3M6 8.25v.25" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <span>{config.error}</span>
          </div>
        )}
      </button>
    )
  }

  return (
    <div
      className="env-modal-overlay"
      onClick={handleOverlayClick}
      data-testid="environment-modal-overlay"
    >
      <div
        className="env-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="env-modal-title"
        data-testid="environment-modal"
      >
        {/* Header */}
        <header className="env-modal-header">
          <span className="env-modal-title" id="env-modal-title">Switch Environment</span>
          <button
            className="env-modal-close-btn"
            onClick={closeModal}
            title="Close"
            aria-label="Close environment switcher"
            data-testid="environment-modal-close-button"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        {/* Environment cards */}
        <div className="env-modal-content">
          <p className="env-modal-description">
            Choose which backend environment to connect to. Switching will reset all active connections.
          </p>

          <div className="env-cards">
            {renderCard('prod')}
            {renderCard('dev')}
          </div>
        </div>

        {/* Footer actions */}
        <footer className="env-modal-footer">
          <button
            type="button"
            className="env-modal-btn secondary"
            onClick={closeModal}
            data-testid="environment-modal-cancel-button"
          >
            Cancel
          </button>
          <button
            type="button"
            className="env-modal-btn primary"
            onClick={handleSwitch}
            disabled={!canSwitch}
            data-testid="environment-modal-switch-button"
          >
            {buttonLabel}
          </button>
        </footer>
      </div>
    </div>
  )
}
